import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from '@remix-run/react';
import { type Screen } from '~/services/layout/types';
import { getServerPreloadPlugins } from '~/services/hooks/general-content-screen-hook';
import { useStableFetcher } from './use-stable-fetcher';
import { addToHistory, removeCurrentFromHistory } from './use-screens-history';

/**
 * Runs server preload hooks for the current screen (login, parent-lock, storefront).
 * Loading and redirects wait until the current request has settled so leftover
 * keyed-fetcher data cannot be treated as the new result.
 *
 * @param screen - Current screen from loader data.
 * @returns `true` while hooks are evaluating or redirecting.
 */
export function useScreenHooks(screen: Screen): boolean {
  const navigate = useNavigate();
  const location = useLocation();

  const serverPreloadPlugins = getServerPreloadPlugins(
    screen?.hooks?.preload_plugins
  );
  const hasScreenHooks = serverPreloadPlugins.length > 0;
  const requestUrl = location.search
    ? `${location.pathname}${location.search}`
    : location.pathname;
  const requestIdentity = `${screen?.id ?? ''}:${requestUrl}:${hasScreenHooks}`;
  const previousRequestIdentityRef = useRef(requestIdentity);
  const requestGenerationRef = useRef(0);

  if (previousRequestIdentityRef.current !== requestIdentity) {
    previousRequestIdentityRef.current = requestIdentity;
    requestGenerationRef.current += 1;
  }

  const loadKey = `${requestIdentity}:${requestGenerationRef.current}`;
  const fetcher = useStableFetcher({
    key:
      hasScreenHooks && screen?.id
        ? `screen-hooks:${screen.id}:${requestUrl}`
        : undefined,
  });
  const [acceptedLoadKey, setAcceptedLoadKey] = useState<string | null>(null);
  const inFlightLoadKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!hasScreenHooks || !screen?.id) return;

    inFlightLoadKeyRef.current = null;
    fetcher.load(
      `/api/maybe-redirect?screenId=${encodeURIComponent(
        screen?.id
      )}&requestUrl=${encodeURIComponent(requestUrl)}`
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, screen?.id, hasScreenHooks]);

  useEffect(() => {
    if (fetcher.state !== 'idle') {
      inFlightLoadKeyRef.current = loadKey;
      return;
    }

    if (inFlightLoadKeyRef.current !== loadKey) return;
    if (!hasScreenHooks) return;

    setAcceptedLoadKey(loadKey);
  }, [fetcher.state, fetcher.data, loadKey, hasScreenHooks]);

  const data =
    fetcher.state === 'idle' && acceptedLoadKey === loadKey
      ? (fetcher.data as { redirectTo?: string } | undefined)
      : undefined;

  useEffect(() => {
    if (data?.redirectTo) {
      const isLockRedirect = data.redirectTo.includes('/lock');

      if (isLockRedirect) {
        const currentPath = location.pathname + location.search;
        removeCurrentFromHistory(currentPath);
      }

      navigate(data.redirectTo, { replace: isLockRedirect });
      return;
    }

    if (data && hasScreenHooks) {
      const currentPath = location.pathname + location.search;
      addToHistory(currentPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const isLoading =
    hasScreenHooks && (acceptedLoadKey !== loadKey || fetcher.state !== 'idle');
  const hasRedirect = Boolean(data?.redirectTo);

  return isLoading || hasRedirect;
}
